<script>
    import Toast from 'bootstrap.toast';
    import { displayValidationSummary, hideValidationSummary, hide, show } from 'ui-utils';
    import { fetch, parseErrors } from 'utils';

    export default function(props = {}) {
        const root = this;

        return {
            submitting: false,
            triggerValue: props.triggerValue,
            useToasts: props.useToasts ?? false,
            resetOnSuccess: props.resetOnSuccess ?? true,
            defaultErrorMessage: props.defaultErrorMessage ?? 'There was a problem processing your request.',
            // Exposing the root instance allows you to access the root data within this component.
            get $root() {
                return root;
            },
            mounted(element, data = this) {
                // Add the submit data event handler.
                element.addEventListener('valid', this.submitHandler);

                // Call the root mounted method.
                root.mounted(element, data);
            },
            unmounted(element, data = this) {
                // Remove the event handler. This make sure when the form is unmounted it's data is not submitted.
                element.removeEventListener('valid', this.submitHandler);

                // Call the root unmounted method.
                root.unmounted(element, data);
            },
            async submitHandler(e) {
                // Make sure the trigger value matches (if applicable).
                if (this.triggerValue && e.detail.trigger.value != this.triggerValue)
                    return;

                e.preventDefault();

                // Trigger the submitting event.
                const cancelled = !this.$refs.root.dispatchEvent(new CustomEvent('submitting', { cancelable: true, detail: { form: this, event: e } }));

                // Submit the form if the default has not been prevented.
                if (!cancelled) {
                    this.submitting = true;

                    const { response, data } = await this.submit();

                    if (response && response.ok) {
                        // Trigger the success event.
                        const cancelled = !this.$refs.root.dispatchEvent(new CustomEvent('success', { cancelable: true, detail: { form: this, event: e, response, data } }));

                        if (!cancelled) {
                            // Reset the form (if applicable).
                            if (this.resetOnSuccess)
                                this.$refs.root.reset();

                            if (this.useToasts)
                                Toast.getOrCreateInstance(this.$refs.root.querySelector('.success')).show();
                            else {
                                show(this.$refs.root.querySelectorAll('.success'), 0);
                                hide(this.$refs.root.querySelectorAll('.form'), 0);
                            }

                            // Hide the validation summary.
                            // Note: If the form does not contain an failure element (with a failure class) then a validation summary may appear regardless of whether we are using toasts.
                            hideValidationSummary(this.$refs.root);
                        }
                    } else {
                        // Trigger the failure event.
                        const cancelled = !this.$refs.root.dispatchEvent(new CustomEvent('failure', { cancelable: true, detail: { form: this, event: e, response, data } }));

                        if (!cancelled) {
                            const failure = this.$refs.root.querySelector('.failure');

                            if (this.useToasts && failure != null)
                                Toast.getOrCreateInstance(failure).show();
                            else {
                                // Get the errors.
                                const errors = parseErrors(data, this.defaultErrorMessage);

                                // Display the validation summary.
                                displayValidationSummary(this.$refs.root, errors);
                            }
                        }
                    }

                    this.submitting = false;

                    // Trigger the submitted event.
                    this.$refs.root.dispatchEvent(new CustomEvent('submitted', { detail: { form: this, event: e, response, data } }));
                }
            },
            async submit(additionalData = {}) {
                const formData = new FormData(this.$refs.root);

                for (const key in additionalData) {
                    formData.append(key, additionalData[key]);
                }

                let response, data = null;

                try {
                    response = await fetch(this.$refs.root.action, {
                        method: this.$refs.root.method,
                        body: formData
                    });

                    // Get the response data.
                    const contentType = response.headers.get('content-type');
                    const isJson = contentType && contentType.includes('json');
                    data = isJson ? await response.json() : await response.text();
                } catch (error) {
                    console.error(error);

                    // This typically throws an exception when uploading a file in the form and then the file changes. Therefore you should reset the form so the user can re-select the file to upload.
                    this.$refs.root.reset();
                }

                return { response, data };
            }
        };
    }
</script>